import { useEffect, useMemo, useState } from "react";
import { v4 } from "uuid";
import { Pencil, Plus, Save, Trash2, X } from "lucide-react";

import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";

import {
  useQpmCompanyReferenceCreateMutation,
  useQpmCompanyReferenceDeleteMutation,
  useQpmCompanyReferenceListQuery,
  useQpmCompanyReferenceUpdateMutation,
} from "@/services/backend/qpm/company-references/api/company-reference-api";
import type {
  CreateRequest,
  ListItem,
  UpdateRequest,
} from "@/services/backend/qpm/company-references/types/company-reference";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";

type Draft = {
  branch: string;
  companyName: string;
};

export function CompanyReferenceListTable() {
  const { companyId } = useActiveCompany();
  const {
    data: list,
    error,
    isLoading,
  } = useQpmCompanyReferenceListQuery({
    company: { active: true, values: [companyId] },
  });

  const [create, createState] = useQpmCompanyReferenceCreateMutation();
  const [update, updateState] = useQpmCompanyReferenceUpdateMutation();
  const [remove, removeState] = useQpmCompanyReferenceDeleteMutation();

  const items: ListItem[] = useMemo(() => list?.items ?? [], [list?.items]);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [items],
  );

  const [newRowId, setNewRowId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const isCreatingNew = editingId != null && editingId === newRowId;

  const [draft, setDraft] = useState<Draft>({
    branch: "",
    companyName: "",
  });

  useEffect(() => {
    if (!editingId) return;

    if (newRowId && editingId === newRowId) {
      setDraft({ branch: "", companyName: "" });
      return;
    }

    const row = sortedItems.find((c) => c.id === editingId);
    if (!row) return;

    setDraft({
      branch: row.branch,
      companyName: row.companyName,
    });
  }, [editingId, newRowId, sortedItems]);

  const onCancel = () => {
    setEditingId(null);
    setNewRowId(null);
    setDraft({ branch: "", companyName: "" });
  };

  const validate = (d: Draft): string | null => {
    if (!d.companyName || d.companyName.trim().length === 0)
      return t("Firmenname fehlt");

    return null;
  };

  const onSave = async () => {
    const validationError = validate(draft);
    if (validationError) return;

    if (isCreatingNew) {
      await create({
        companyId,
        branch: draft.branch,
        companyName: draft.companyName,
      } satisfies CreateRequest).unwrap();
      onCancel();
      return;
    }

    if (!editingId) return;

    await update({
      id: editingId,
      branch: draft.branch,
      companyName: draft.companyName,
    } satisfies UpdateRequest).unwrap();

    onCancel();
  };

  if (error) return <RTKQueryErrorAlert error={error} />;
  if (isLoading) return <Skeleton className="h-20 w-full" />;

  const canAddRow = newRowId == null && editingId == null;

  return (
    <div className="flex flex-col gap-3">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[20%]">{t("Branche")}</TableHead>
            <TableHead>{t("Firmenname")}</TableHead>
            <TableHead className="w-[4%] text-right">
              {t("Bearbeiten")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedItems.map((it) => (
            <CompanyReferenceRow
              key={it.id}
              item={it}
              isEditMode={editingId === it.id}
              isNew={false}
              draft={draft}
              setDraft={setDraft}
              onEdit={() => setEditingId(it.id)}
              onCancel={onCancel}
              onSave={onSave}
              onDelete={async () => {
                await remove({ id: it.id }).unwrap();
              }}
            />
          ))}

          {newRowId && editingId === newRowId && (
            <CompanyReferenceRow
              key={newRowId}
              item={{
                id: newRowId,
                branch: draft.branch,
                companyName: draft.companyName,
              }}
              isEditMode
              isNew
              draft={draft}
              setDraft={setDraft}
              onEdit={() => setEditingId(newRowId)}
              onCancel={onCancel}
              onSave={onSave}
              onDelete={undefined}
            />
          )}

          {sortedItems.length === 0 && !newRowId && (
            <TableRow>
              <TableCell colSpan={3} className="text-muted-foreground py-6">
                {t("Keine Einträge gefunden.")}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <div className="flex justify-start">
        <Button
          disabled={!canAddRow}
          onClick={() => {
            const id = v4();
            setNewRowId(id);
            setEditingId(id);
            setDraft({ branch: "", companyName: "" });
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t("Firmenreferenz hinzufügen")}
        </Button>
      </div>

      {(createState.error || updateState.error || removeState.error) && (
        <RTKQueryErrorAlert
          error={(createState.error || updateState.error || removeState.error)!}
        />
      )}
    </div>
  );
}

function CompanyReferenceRow({
  item,
  isEditMode,
  isNew,
  draft,
  setDraft,
  onEdit,
  onCancel,
  onSave,
  onDelete,
}: {
  item: ListItem;
  isEditMode: boolean;
  isNew: boolean;
  draft: Draft;
  setDraft: (d: Draft) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  onDelete?: () => void;
}) {
  if (isEditMode) {
    return (
      <TableRow>
        <TableCell className="py-1.5">
          <Input
            value={draft.branch}
            placeholder={t("Branche")}
            onChange={(e) => setDraft({ ...draft, branch: e.target.value })}
          />
        </TableCell>
        <TableCell className="py-1.5">
          <Input
            value={draft.companyName}
            placeholder={t("Firmenname")}
            onChange={(e) =>
              setDraft({ ...draft, companyName: e.target.value })
            }
          />
        </TableCell>
        <TableCell className="py-1.5 text-right">
          <div className="flex items-center justify-end">
            <TooltipProvider>
              {!isNew && onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="mr-1"
                      size="sm"
                      variant="outline"
                      onClick={onDelete}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("Löschen")}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="mr-1"
                    size="sm"
                    variant="outline"
                    onClick={onCancel}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Abbrechen")}</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button size="sm" variant="default" onClick={onSave}>
                    <Save className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t("Speichern")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell className="py-1.5">{item.branch}</TableCell>
      <TableCell className="py-1.5">{item.companyName}</TableCell>
      <TableCell className="py-1.5 text-right">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("Bearbeiten")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
    </TableRow>
  );
}
